import { ItemTypeEntity } from '@entities/ItemType/Types';
import { ItemEntity } from '@entities/Item/Types';
import { GetItemsTotalPrice } from '@viewModels/FragmentsViewModel/Services/GetItemsTotalPrice';
import { useItemsSelectionStore } from '../../Stores/ItemsSelectionStore';
import { useFragmentsStore } from '../../Stores/FragmentsStore';
import { IsItemSelected } from '@viewModels/ItemsSelectionViewModel/Services/IsItemSelected';
import { GetParsedFragmentItemsWithoutLabour } from '@viewModels/ItemsSelectionViewModel/Services/GetParsedFragmentItemsWithoutLabour';
import { logDOM } from '@testing-library/react';

export class ItemsSelectionViewModel {
    getSelectedTotalByItemTypes = (itemTypes: ItemTypeEntity[]) => {
        const { selectedItems } = useItemsSelectionStore.getState();

        const items = itemTypes
            .filter(item => item.items && item.items.length > 0)
            .flatMap(item => item.items)
            .filter(Boolean) as ItemEntity[];

        const itemUuids = items.map(item => item.uuid);
        const filteredSelectedItems = selectedItems.filter(item =>
            itemUuids.includes(item.uuid)
        );

        return GetItemsTotalPrice(filteredSelectedItems);
    };

    getSelectedItemsGrandTotal = () => {
        const { selectedItems } = useItemsSelectionStore.getState();
        return GetItemsTotalPrice(selectedItems);
    };

    getSelectedToolItemsTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();
        const { selectedItems } = useItemsSelectionStore.getState();

        const itemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );
        const toolItems = itemTypes
            .filter(itemType => itemType.tool)
            .flatMap(itemType => itemType.items)
            .filter(Boolean) as ItemEntity[];
        const selectedToolItems = toolItems.filter(item =>
            IsItemSelected(item.uuid, selectedItems)
        );

        return GetItemsTotalPrice(selectedToolItems);
    };

    getSelectedMaterialItemsTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();
        const { selectedItems } = useItemsSelectionStore.getState();

        const itemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );
        const materialItems = itemTypes
            .filter(itemType => !itemType.tool && !itemType.labor)
            .flatMap(itemType => itemType.items)
            .filter(Boolean) as ItemEntity[];
        const selectedMaterialItems = materialItems.filter(item =>
            IsItemSelected(item.uuid, selectedItems)
        );

        return GetItemsTotalPrice(selectedMaterialItems);
    };

    selectAllFragmentItems = (fragmentUuid: string) => {
        const { selectedItems } = useItemsSelectionStore.getState();
        const { parsedFragments } = useFragmentsStore.getState();

        const fragmentItems = GetParsedFragmentItemsWithoutLabour(
            fragmentUuid,
            parsedFragments
        );

        const selectedItemIds = new Set(selectedItems.map(item => item.id));
        const newItemsToAdd = fragmentItems.filter(
            item => !selectedItemIds.has(item.id)
        );

        if (newItemsToAdd.length === 0) {
            return;
        }

        useItemsSelectionStore.setState({
            selectedItems: [...selectedItems, ...newItemsToAdd],
        });
    };

    removeAllFragmentItemsFromSelected = (fragmentUuid: string) => {
        const { selectedItems } = useItemsSelectionStore.getState();
        const { parsedFragments } = useFragmentsStore.getState();

        const fragmentItems = GetParsedFragmentItemsWithoutLabour(
            fragmentUuid,
            parsedFragments
        );
        const fragmentItemUuids = fragmentItems.map(item => item.uuid);
        const filteredSelectedItems = selectedItems.filter(
            item => !fragmentItemUuids.includes(item.uuid)
        );

        useItemsSelectionStore.setState({
            selectedItems: filteredSelectedItems,
        });
    };

    toggleSelectItem = (item: ItemEntity) => {
        const { selectedItems } = useItemsSelectionStore.getState();

        if (IsItemSelected(item.uuid, selectedItems)) {
            const filteredItems = selectedItems.filter(
                selectedItem => selectedItem.uuid !== item.uuid
            );

            useItemsSelectionStore.setState({ selectedItems: filteredItems });
            return;
        }

        useItemsSelectionStore.setState({
            selectedItems: [...selectedItems, item],
        });
    };

    selectAllItems = () => {
        const { parsedFragments } = useFragmentsStore.getState();

        const itemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );

        const filteredItemTypesWithoutLabour = itemTypes.filter(
            itemType => !itemType.labor
        );
        const items = filteredItemTypesWithoutLabour
            .flatMap(itemType => itemType.items)
            .filter(Boolean) as ItemEntity[];

        useItemsSelectionStore.setState({ selectedItems: items });
    };

    clearAllSelectedItems = () => {
        useItemsSelectionStore.setState({ selectedItems: [] });
    };

    selectAllItemsFromItemTypes = (itemTypes: ItemTypeEntity[]) => {
        const { selectedItems } = useItemsSelectionStore.getState();

        const items = itemTypes
            .filter(item => item.items && item.items.length > 0)
            .flatMap(item => item.items)
            .filter(Boolean) as ItemEntity[];

        const selectedItemIds = new Set(selectedItems.map(item => item.id));
        const newItemsToAdd = items.filter(
            item => !selectedItemIds.has(item.id)
        );

        if (newItemsToAdd.length === 0) {
            return;
        }

        useItemsSelectionStore.setState({
            selectedItems: [...selectedItems, ...newItemsToAdd],
        });
    };

    removeAllSelectedItemsFromItemTypes = (itemTypes: ItemTypeEntity[]) => {
        const { selectedItems } = useItemsSelectionStore.getState();

        const items = itemTypes
            .filter(item => item.items && item.items.length > 0)
            .flatMap(item => item.items)
            .filter(Boolean) as ItemEntity[];

        const itemUuids = items.map(item => item.uuid);
        const filteredSelectedItems = selectedItems.filter(
            item => !itemUuids.includes(item.uuid)
        );

        useItemsSelectionStore.setState({
            selectedItems: filteredSelectedItems,
        });
    };
}
